import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { navigate } from 'gatsby'
import cx from 'clsx'
import { useSpring, animated, config } from 'react-spring'
// Redux
import { setSort, setYear } from '~lib/saga/filter/actions'
// Components
import Link from '~2023/components/Link'
import { MenuIcon } from '~2023/components/Menu'
// Styles
import useStyles from './Menu.style'

const filterSelector = (state) => state.filter

const Menu = () => {
  const classes = useStyles()
  const [menuVisibility, setMenuVisibility] = useState(false)
  const menuElement = useRef(null)
  // const lastMenuElement = useRef(null)

  const dispatch = useDispatch()
  const {
    data: { year: filterYear }, // eslint-disable-line no-unused-vars
    default: { year: defaultYear },
    years,
  } = useSelector(filterSelector)

  const handleYear = (event, year) => {
    event.preventDefault()
    dispatch(setYear(year))
    // @note[request] If we move between years, force `rank` in case chosen year does not have sort capabilities
    dispatch(setSort('rank'))
    navigate(defaultYear === year ? '/' : `/${year}`)
  }

  const handleClick = () => {
    setMenuVisibility(!menuVisibility)
  }

  const handleEscapeKey = (e) => {
    if (27 === e.keyCode) setMenuVisibility(false)
  }

  const handleClickOutside = (e) => {
    const { target } = e
    if (menuElement.current && !menuElement.current.contains(target)) {
      setMenuVisibility(false)
    }
  }

  const handleCloseOnBlur = () => {
    setMenuVisibility(false)
  }

  const { opacity, maxHeight } = useSpring({
    config: config.default,
    maxHeight: `${menuVisibility ? 510 : 0}px`,
    opacity: menuVisibility ? 1 : 0,
  })

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey, false)
    document.addEventListener('click', handleClickOutside, false)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, false)
      document.removeEventListener('click', handleClickOutside, false)
    }
  })

  return (
    <nav className={cx(classes.siteNavigation)} ref={menuElement}>
      <button
        aria-controls="siteMenu"
        aria-expanded={menuVisibility}
        aria-haspopup="true"
        className={cx(classes.menuButton)}
        id="siteMenuBtn"
        onClick={() => handleClick()}
        tabIndex={null}
      >
        <MenuIcon
          desc="3 bars aligned vertically"
          height={22}
          isOpen={menuVisibility}
          title="FanSided Menu"
          width={22}
        />
      </button>
      <animated.ul
        aria-hidden={!menuVisibility}
        aria-labelledby="siteMenuBtn"
        className={cx(classes.siteMenu)}
        id="siteMenu"
        role="menu"
        style={{ maxHeight, opacity }}
      >
        <li role="none" className={cx(classes.menuListItem)}>
          <Link className={cx(classes.menuItem)} to={`/${defaultYear}/about`}>
            About / Methods
          </Link>
        </li>
        <li className={cx(classes.menuListItem)}>
          <span className={cx(classes.menuItemHeader)}>Years</span>
          {years && (
            <ul className={cx(classes.subMenu)}>
              {years.map((year) => (
                <li
                  role="none"
                  className={cx(classes.subMenuItem)}
                  key={`year--${year.id}`}
                >
                  <Link
                    className={cx(classes.menuItem)}
                    onClick={(event) => handleYear(event, year.id)}
                    role="menuitem"
                    to={defaultYear === year.id ? `/` : `/${year.id}`}
                  >
                    <span className="screen-reader-text">Visit the</span>{' '}
                    {year.id}
                    <span className="screen-reader-text"> Fandoms</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
        <li className={cx(classes.menuListItem)}>
          <span className={cx(classes.menuItemHeader)}>
            <span className="screen-reader-text">Visit the</span> Main Site
          </span>
          <ul className={cx(classes.subMenu)}>
            <li role="none">
              <a
                className={cx(classes.menuItem)}
                href="https://fansided.com"
                rel="noopener noreferrer"
                onBlur={() => handleCloseOnBlur()}
                role="nofollow noreferrer noopenner"
                target="_blank"
              >
                <span className="screen-reader-text">Navigate to</span>{' '}
                FanSided.com
              </a>
            </li>
          </ul>
        </li>
      </animated.ul>
    </nav>
  )
}

export default Menu
